import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatchApp } from "../../../../lib/redux";
import { useInView } from "react-intersection-observer";
import { useEffect, useState } from "react";
import { Box, Typography, Paper } from "@mui/material";
import Loader from "../../../../components/Loaders/Loader";
import {
  startGetCampaignContext,
  startGetCampaignLogs,
} from "../../../../actions/adsCampaigns";
import LogEntry from "./components/LogEntry";
import {
  ADS_CAMPAIGNS_LOGS_CONTEXT_TITLE,
  EMPTY_CHANGES,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";
import { BORDER_RADIUS, ROUTE_CAMPAIGNS } from "../../../../utils/constants";

const CampaignLogs = ({
  sx = {},
  enableReverse = false,
  reverseContext = null,
}) => {
  const [cachedNameEntities, setCachedNameEntities] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [logs, setLogs] = useState({});
  const [lastKey, setLastKey] = useState(null);
  const [campaignContext, setCampaignContext] = useState(null);
  const [params] = useSearchParams();
  const campaignID = params.get("campaignID");

  const navigate = useNavigate();
  const dispatch = useDispatchApp();

  const { t } = useTranslationApp();

  const { ref, inView } = useInView({
    triggerOnce: false,
    rootMargin: "20px 0px",
  });

  useEffect(() => {
    const getCampaignContext = async () => {
      const responseContext = await dispatch(
        startGetCampaignContext(campaignID)
      );
      if (responseContext.ok) {
        setCampaignContext(responseContext.context);
      }
    };

    if (campaignID) {
      getCampaignContext();
    }
  }, [campaignID, dispatch]);

  const getInitialData = async () => {
    if (!campaignID) {
      navigate(ROUTE_CAMPAIGNS);
      return;
    }

    const response = await dispatch(
      startGetCampaignLogs({
        campaignID,
        onChangeLastKey: setLastKey,
      })
    );
    if (response.ok) {
      const { data = {} } = response;
      setLogs(data);
    }

    setIsLoading(false);
  };

  const onGetNextLogs = async () => {
    if (isLoading || isLoadingMore) return;

    setIsLoadingMore(true);
    const response = await dispatch(
      startGetCampaignLogs({
        campaignID,
        lastKey,
        onChangeLastKey: setLastKey,
      })
    );

    if (response.ok) {
      const { data = {} } = response;
      setLogs((prevLogs) => ({
        ...prevLogs,
        ...data,
      }));
    }
    setIsLoadingMore(false);
  };

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (inView) {
      onGetNextLogs();
    }
    // eslint-disable-next-line
  }, [inView]);

  if (isLoading) return <Loader fullWidth />;

  // Convert logs object to array and sort by creationTime (newest first)
  const logsArray = Object.values(logs).sort(
    (a, b) => b.creationTime - a.creationTime
  );

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: "transparent",
        backgroundImage: "none",
        px: {
          xs: 1,
          sm: 2,
        },
        ...sx,
      }}
    >
      <Box
        sx={{
          my: 2,
          maxHeight: 500,
          overflowY: "auto",
          pr: 4,
          mr: -2,
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0, 0, 0, 0.1)",
            borderRadius: "4px",
          },
        }}
      >
        <CampaignContext campaignContext={campaignContext} t={t} />
        {logsArray.length === 0 ? (
          <Typography align="center" sx={{ py: 4 }}>
            {t(EMPTY_CHANGES)}
          </Typography>
        ) : (
          logsArray.map((log, index) => (
            <LogEntry
              key={log.creationTime}
              log={log}
              isLast={index === logsArray.length - 1}
              cachedNameEntities={cachedNameEntities}
              onChangeCachedNameEntities={(newEntities) =>
                setCachedNameEntities((prev) => ({
                  ...prev,
                  ...newEntities,
                }))
              }
              enableReverse={enableReverse}
              reverseContext={reverseContext}
            />
          ))
        )}
        <Box
          ref={ref}
          sx={{
            minHeight: 20,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 2,
            mb: 2,
          }}
        >
          {isLoadingMore && <Loader size={24} hasMarginTop={false} />}
        </Box>
      </Box>
    </Paper>
  );
};

const CampaignContext = ({ campaignContext, t }) => {
  if (!campaignContext) return null;

  return (
    <>
      <Typography variant="subtitle1" component="div" sx={{ mb: 2, pt: 1 }}>
        {t(ADS_CAMPAIGNS_LOGS_CONTEXT_TITLE)}
      </Typography>
      <Box
        sx={{
          p: 2,
          backgroundColor: "fields.background",
          border: `1px solid ${INPUT_COLOR_BORDER}`,
          borderRadius: BORDER_RADIUS,
          fontSize: "14px",
          mb: 3,
        }}
      >
        {campaignContext}
      </Box>
    </>
  );
};

export default CampaignLogs;
